"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { FREE_TOOLS } from "@/config/freeTools";
import { toolPath } from "@/libs/peeqdPaths";
import { PeeqdLogoIcon, ToolIcon, InstagramIcon, TikTokIcon } from "./icons";

interface PeeqdSidebarProps {
  onClose?: () => void;
}

export default function PeeqdSidebar({ onClose }: PeeqdSidebarProps) {
  const pathname = usePathname();
  const instagramTools = FREE_TOOLS.filter((t) => t.platform === "instagram");

  const isActive = (slug: string) => pathname === toolPath(slug);

  return (
    <aside className="flex h-full w-60 flex-col border-r border-line bg-surface/80 backdrop-blur">
      <div className="flex h-16 items-center gap-2.5 px-5">
        <Link href="/" className="flex items-center gap-2.5" onClick={onClose}>
          <PeeqdLogoIcon className="h-8 w-8 shadow-soft" />
          <span className="font-display text-[19px] font-extrabold tracking-tight">Peeqd</span>
        </Link>
        <span className="ml-auto rounded-full border border-line px-1.5 py-0.5 font-mono text-[10px] text-muted">
          beta
        </span>
      </div>

      <nav className="flex-1 space-y-4 overflow-y-auto px-3 py-4">
        <div>
          <p className="flex items-center gap-2 pb-2 font-display text-[11px] font-extrabold uppercase tracking-widest text-muted">
            <InstagramIcon className="h-3.5 w-3.5 shrink-0 text-rose/70" />
            Instagram
          </p>
          <div className="space-y-1">
            {instagramTools.map((tool) => (
              <Link
                key={tool.id}
                href={toolPath(tool.slug)}
                onClick={onClose}
                data-active={isActive(tool.slug) ? "true" : "false"}
                className="tool-link relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-[14px] font-semibold text-muted transition-colors hover:text-ink"
              >
                <span className="absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r-full grad-bg opacity-0 transition-opacity" />
                <ToolIcon icon={tool.icon} />
                {tool.shortTitle}
              </Link>
            ))}
          </div>
        </div>

        <div>
          <p className="flex items-center gap-2 pb-2 font-display text-[11px] font-extrabold uppercase tracking-widest text-muted">
            <TikTokIcon className="h-3.5 w-3.5 shrink-0 text-ink/50" />
            TikTok
          </p>
          <div
            className="mx-1 rounded-xl border border-dashed border-line bg-bg/60 px-3 py-3 text-center"
            aria-label="TikTok tools coming soon"
          >
            <p className="font-display text-[14px] font-bold text-muted">Coming soon</p>
            <p className="mt-1 text-[12px] leading-snug text-muted/80">
              TikTok analytics coming soon for Peeqd Pro users
            </p>
          </div>
        </div>
      </nav>
    </aside>
  );
}
